import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { Accordion } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faTimesCircle } from "@fortawesome/free-solid-svg-icons";

import "react-datepicker/dist/react-datepicker.css";

import CustomButton from "../../../components/CustomButton";
import CustomModal from "../../../components/CustomModal";

const CertificatesModal = ({
  show,
  onClose,
  items,
  formatDate,
  onIssue,
  isIssuing,
}) => {
  const [arrivalDate, setArrivalDate] = useState(null);
  const [departureDate, setDepartureDate] = useState(null);
  const [comment, setComment] = useState("");
  const [showDateError, setShowDateError] = useState(false);

  const resetDraft = () => {
    setArrivalDate(null);
    setDepartureDate(null);
    setComment("");
    setShowDateError(false);
  };

  useEffect(() => {
    if (show) {
      resetDraft();
    }
  }, [show]);

  useEffect(() => {
    if (arrivalDate || departureDate) {
      setShowDateError(false);
    }
  }, [arrivalDate, departureDate]);

  // removed issue type step: modal now shows the form and student list together

  const renderDetailsStep = () => (
    <div className="text-start">
      {/* <div className="body mb-3">
        Fill arrival or departure dates (optional) and add an optional comment
        for the selected student(s) below.
      </div> */}
      <div className="certificates-date-grid mb-3">
        <div>
          <label className="label-style mb-2">Arrival date (optional)</label>
          <DatePicker
            selected={arrivalDate}
            onChange={(date) => setArrivalDate(date)}
            onBlur={() => {
              if (!arrivalDate && !departureDate) setShowDateError(true);
            }}
            maxDate={departureDate || undefined}
            dateFormat="dd/MM/yyyy"
            placeholderText="Select date"
            className={`form-control ${showDateError ? "is-invalid" : ""}`}
            popperPlacement="bottom-start"
            calendarClassName="certificates-date-picker"
          />
        </div>
        <div>
          <label className="label-style mb-2">Departure date (optional)</label>
          <DatePicker
            selected={departureDate}
            onChange={(date) => setDepartureDate(date)}
            onBlur={() => {
              if (!arrivalDate && !departureDate) setShowDateError(true);
            }}
            minDate={arrivalDate || undefined}
            dateFormat="dd/MM/yyyy"
            placeholderText="Select date"
            className={`form-control ${showDateError ? "is-invalid" : ""}`}
            popperPlacement="bottom-start"
            calendarClassName="certificates-date-picker"
          />
        </div>
      </div>
      {showDateError && (
        <div className="invalid-feedback d-block mt-2 certificates-date-error">
          Please select an arrival or a departure date for the selected student(s).
        </div>
      )}
      <div>
        <label className="label-style mb-2">Comment (optional)</label>
        <textarea
          className="form-control mb-3"
          rows={3}
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          placeholder="Add a comment"
        />
      </div>
    </div>
  );

  const renderStudentList = () => (
    <div className="text-start certificates-modal-list">
      <h6 className="mb-3">Selected Student(s):</h6>
      {items.length ? (
        <Accordion id="accordion-certificates-students" className="certificates-students-accordion">
          {items.map((item, index) => (
            <Accordion.Item key={`${item.type}-${item.id}`} eventKey={index}>
              <Accordion.Header>
                {item.studentFirstName} {item.studentLastName}
              </Accordion.Header>
              <Accordion.Body>
                <div>
                  <div className="mb-3 d-flex align-items-center gap-2 certificates-status-indicator">
                    <FontAwesomeIcon
                      icon={item.hasCertificate ? faCheckCircle : faTimesCircle}
                      className={item.hasCertificate ? "text-success" : "text-secondary"}
                      size="lg"
                    />
                    <span className="fw-500">
                      {item.hasCertificate ? "Certificate issued" : "No certificate issued yet"}
                    </span>
                  </div>
                  <div className="mb-2">
                    <strong>Action:</strong> {item.hasCertificate ? "Update existing Certificate" : "Create new Certificate"}
                  </div>
                  {item.hasCertificate && (
                    <div className="mb-2">
                      <strong>Certificate title:</strong> {item.certificateTitle || item.existingCosId || "-"}
                    </div>
                  )}
                  <div className="mb-2">
                    <strong>Planned mobility period as per OLA data:</strong> {formatDate(item.plannedPeriodFrom)} - {formatDate(item.plannedPeriodTo)}
                  </div>
                  <div className="mb-2">
                    <strong>Academic year:</strong> {item.academicYear || "-"}
                  </div>
                  <div className="mb-2">
                    <strong>Email:</strong> {item.studentEmail || "-"}
                  </div>
                  <div className="mb-2">
                    <strong>Birthdate:</strong> {item.studentBirthdate || "-"}
                  </div>
                  {/* <div className="mb-2">
                    <strong>ESI:</strong> {item.studentEsi || "-"}
                  </div> */}
                  <div className="mb-2">
                    <strong>Sending HEI Erasmus Code:</strong> {item.sendingInstitutionHei || item.sendingInstitutionHeiId || "-"}
                  </div>
                  <div>
                    <strong>Receiving HEI Erasmus Code:</strong> {item.receivingInstitutionHei || item.receivingInstitutionHeiId || "-"}
                  </div>
                </div>
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      ) : (
        <div className="body">No certificates selected.</div>
      )}
    </div>
  );

  const handleIssue = async () => {
    if (!onIssue) {
      return;
    }

    // guard: if both dates empty, show validation and don't submit
    if (!arrivalDate && !departureDate) {
      setShowDateError(true);
      return;
    }

    const success = await onIssue({
      arrivalDate,
      departureDate,
      comment,
    });

    if (success) {
      onClose();
    }
  };

  const body = (
    <div>
      {renderDetailsStep()}
      {renderStudentList()}
    </div>
  );

  return (
    <CustomModal
      show={show}
      handleClose={onClose}
      title="Issue Certificate"
      subtitle={
        "Fill arrival or departure dates for the selected student(s). You may provide either one date or both"
      }
      body={body}
      hasIcon={false}
      bodyClassName="certificates-modal-body"
      hasCancelButton={false}
      button={
        <div className="d-flex w-100 align-items-center">
          <div>
            <CustomButton buttonType="outlined" text="Cancel" handleClick={onClose} />
          </div>
          <div className="ms-auto">
            <CustomButton
              buttonType="orange"
              text="Issue Certificate"
              handleClick={handleIssue}
              disabled={isIssuing || (!arrivalDate && !departureDate)}
            />
          </div>
        </div>
      }
    />
  );
};

export default CertificatesModal;
